// ============================================================
// TILT — DUEL SCREEN  (the flagship)
// ------------------------------------------------------------
// SPLIT-SCREEN SABOTAGE. Two tables side by side. Yours is big
// and warm. Theirs is smaller, colder, and one tap away from
// being ruined.
//
// ── HOW A DUEL ACTUALLY WORKS ────────────────────────────────
//   • You each simulate YOUR OWN table, locally, at full speed.
//     Nobody's physics crosses the wire. Ever.
//   • The ONLY thing transmitted is a sabotage: a power id and
//     a point. ~12 bytes.
//   • Latency is therefore IRRELEVANT. A 300ms delay on an
//     incoming Anchor is indistinguishable from your opponent
//     having hesitated for 300ms. There is nothing to desync
//     because there is nothing shared.
//
// That single decision is what makes PvP shippable. A shared-
// physics duel would need rollback netcode and would still feel
// bad. This feels PERFECT and it took one file (SabotageBus).
//
// ── THE CORE TENSION ─────────────────────────────────────────
// One charge meter. Two ways to spend it.
//
//   Spend on YOURSELF  → you score more.
//   Spend on THEM      → they score less.
//
// Every single time the meter fills, the game asks you that
// question. Answering it ten times a minute IS the duel. There
// is no correct answer, which is why it's a game and not a math
// problem.
//
// ── THE COLOR LAW ────────────────────────────────────────────
// Self powers are COOL (cyan). Sabotage is HOT (magenta). A
// player mid-panic must NEVER confuse them. Color is the safety
// rail, not decoration.
// ============================================================

import { useEffect, useRef, useState, useCallback } from "react";
import { UI, PF, PAGE_BG, PANEL, PANEL_LIT, facet, facetTB,
         RAISED, glow, bloom, ensureKeyframes } from "../theme";
// ★ THE 3D DECK — same snapshot, same props. See SoloScreen.
import Playfield from "../Playfield3D";
import useTiltEngine from "../useTiltEngine";
import { TiltEngine, PHASE } from "../TiltEngine";
import { TiltNPC } from "../TiltNPC";
import {
  ScoreBoard, ChargeMeter, StatusStrip, PowerBar,
  SabotageBar, Ticker, Plunger, Banner, BossBar, MonsterBar, btnStyle,
} from "../HUD";
import Bus, {
  registerTransport, unregisterTransport,
  registerInbox, unregisterInbox, LocalTransport, send, resetBus,
  registerResultInbox, sendResult,
} from "../SabotageBus";
import { getPower } from "../Powers";
import { BOSS_MOVES, GUARDIAN_MOVES } from "../BossKit";

export default function DuelScreen({
  table,
  loadout,
  opponent,          // a GAUNTLET entry (NPC duel) — or null for PvP
  pvp,               // { duelId, myUid, oppUid, db, ...fb } — PvP only
  onExit,
  onResult,
}) {
  ensureKeyframes();

  const [oppSnap, setOppSnap] = useState(null);
  const [taunt, setTaunt]     = useState(null);
  const [winner, setWinner]   = useState(null);

  const npcRef       = useRef(null);
  const npcEngineRef = useRef(null);
  const npcRafRef    = useRef(0);
  const npcLastRef   = useRef(0);

  // ── D6: lifetime stats. Counted here, committed ONCE with the
  //    result (one event = one commit — same law as App.push).
  const statsRef = useRef({ launches: 0, drains: 0, sabotagesSent: 0, sabotagesEaten: 0 });

  // ── MY TABLE ───────────────────────────────────────────────
  const me = useTiltEngine(table, {
    loadout,
    owner: "me",
    // ── WHO DRIVES THE BEING ON MY TABLE ──────────────────────
    // Real PvP: it's the OPPONENT's avatar and THEY pay to swing
    //   it → "remote" (the solo AI is OFF; every move that lands
    //   here is a spend of theirs, off the wire).
    // NPC duel: it's the NPC's avatar acting on its own → "ai".
    bossDriven: pvp ? "remote" : "ai",
    onEvent: (ev) => {
      if (ev.type === "launch") statsRef.current.launches += 1;
      if (ev.type === "drain")  statsRef.current.drains += 1;
      if (ev.type === "sabotaged") statsRef.current.sabotagesEaten += 1;
      if (ev.type === "over") finish();
      // ★ THE FELL IS THE WIN. The being on MY table is the
      //   OPPONENT'S avatar (roadmap T0.22.7, the BossKit header,
      //   and the label on the bar below all say so) — I fell it
      //   by playing the Cirque, and felling it beats THEM.
      //
      //   ⚠ THIS WAS SHIPPED INVERTED (`finishByFell("them")`) —
      //   felling the Ringmaster declared the NPC the winner. The
      //   50-assertion harness never caught it because the harness
      //   proves the ENGINE's laws; the result mapping lives in
      //   React, one layer above every proof. A law that crosses a
      //   layer boundary needs a witness on BOTH sides.
      if (ev.type === "boss_felled") finishByFell("me");
    },
  });

  // ── THEIR TABLE ────────────────────────────────────────────
  // In an NPC duel we run a SECOND, REAL TiltEngine right here.
  // It is not a fake score curve. It is the same class, on the
  // same table, obeying the same physics, and it can lose.
  useEffect(() => {
    if (pvp) return;             // real PvP: the opponent runs on their machine
    if (!opponent) return;

    // The NPC's table needs its OWN mode state — two engines sharing
    // one table object would share `_state` and light each other's
    // targets. So: a shallow clone with a fresh state slot.
    const oppTable = { ...table, _state: null, seed: (table.seed || 1) + 777 };

    const engine = new TiltEngine(oppTable, {
      owner: "npc",
      seed: oppTable.seed,
      loadout: table.loadout || undefined,
      // The opponent's avatar is driven from MY side of the wire
      // (my MonsterBar spends), so its engine must not also
      // auto-swing it. "remote" = no local AI on their being.
      bossDriven: "remote",
      onEvent: (ev) => {
        if (ev.type === "over") finish();
        // ★ The being on THEIR table is MY avatar (the duel model,
        //   T0.22.7). The NPC felling it means they beat ME.
        //   (Was inverted — see the twin note on my engine above.)
        if (ev.type === "boss_felled") finishByFell("them");
      },
    });
    npcEngineRef.current = engine;

    const npc = new TiltNPC(engine, {
      skill: opponent.skill,
      temperament: opponent.temperament,
      name: opponent.name,
      // The NPC declares a sabotage; the BUS carries it to me.
      // The NPC has no idea the bus exists. Zero circular imports —
      // exactly Terror's NPC ↔ ResponseBridge separation.
      onSabotage: (powerId, point) => {
        Bus.receive({
          p: powerId,
          x: point?.x ?? null,
          y: point?.y ?? null,
          from: opponent.name,
          t: Date.now(),
          seq: Math.floor(Math.random() * 1e9),
        });
      },
      onTaunt: (line) => {
        setTaunt(line);
        setTimeout(() => setTaunt(null), 2600);
      },
    });
    npcRef.current = npc;

    // My inbox: sabotages land on MY engine.
    registerInbox(({ powerId, point, from }) => {
      me.engineRef.current?.receiveSabotage(powerId, point, from);
    });

    // My outbound transport: sabotages I cast land on the NPC's engine.
    // The 140ms delay exercises the same async path PvP uses — testing
    // the async path in single-player is how you avoid discovering
    // race conditions on launch day.
    registerTransport(LocalTransport((payload) => {
      const e = npcEngineRef.current;
      if (!e) return;
      const v = Bus.validate(payload);
      if (v) e.receiveSabotage(v.powerId, v.point, v.from);
    }, 140));

    // The NPC's own frame loop. Separate rAF — it must not be
    // coupled to my render cycle.
    const loop = (t) => {
      npcRafRef.current = requestAnimationFrame(loop);
      const e = npcEngineRef.current;
      const n = npcRef.current;
      if (!e || !n) return;
      const last = npcLastRef.current || t;
      const dt = Math.min((t - last) / 1000, 0.25);
      npcLastRef.current = t;

      // The NPC observes a SNAPSHOT of my table — never a live
      // reference. Same discipline as the renderer. Nothing outside
      // the engine gets to hold a pointer into physics.
      const mySnap = me.engineRef.current?.snapshot();
      if (mySnap) n.observe({ ...mySnap, W: table.W, H: table.H });

      n.tick(dt);
      e.tick(dt);
    };
    npcRafRef.current = requestAnimationFrame(loop);

    // Throttled snapshot of THEIR table for my HUD.
    const snapTimer = setInterval(() => {
      const e = npcEngineRef.current;
      if (e) setOppSnap(e.snapshot());
    }, 60);

    return () => {
      cancelAnimationFrame(npcRafRef.current);
      clearInterval(snapTimer);
      resetBus();
      npcRef.current = null;
      npcEngineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, opponent, pvp]);

  // ── PvP wiring ─────────────────────────────────────────────
  useEffect(() => {
    if (!pvp) return;
    registerInbox(({ powerId, point, from }) => {
      me.engineRef.current?.receiveSabotage(powerId, point, from);
    });
    // ── P2.8 / D19: THE RESULT PACKET ─────────────────────────
    // My avatar lives on THEIR machine. If they fell it, my own
    // boss_felled never fires (my local being is fine) — this is
    // the only way I learn I lost. Receiving `r:"fell"` always
    // means it was sent TO the loser; no perspective math.
    registerResultInbox(({ r }) => {
      if (r === "fell") finishByFell("them");
    });
    registerTransport(Bus.FirebaseTransport(pvp));
    return () => { resetBus(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvp]);

  // ── Result: the FELL ───────────────────────────────────────
  // A fell ends the duel outright — it is the cleanest win in the
  // game: you didn't outscore them, you beat their KEEPER. Guarded
  // so it fires once even if both a fell and an "over" race in.
  const finishByFell = useCallback((who) => {
    setWinner((w) => {
      if (w) return w;                         // already decided
      // ── P2.8: tell the LOSER. Their copy of their avatar is
      //    fine, so their boss_felled never fired. Only ever sent
      //    on MY win — a result packet is always addressed to the
      //    machine that lost. (NPC duels: redundant, harmless —
      //    the local transport's validate drops it.)
      if (who === "me") sendResult("fell", "me");
      const mine  = me.engineRef.current;
      const yours = npcEngineRef.current;
      onResult?.({
        won: who === "me", byFell: true,
        myScore: mine?.score ?? 0,
        oppScore: yours?.score ?? oppSnap?.score ?? 0,
        opponent: opponent?.id,
        stats: { ...statsRef.current },        // D6: one event, one commit
      });
      return who;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppSnap, opponent, onResult]);

  // ── Result: the score-out ──────────────────────────────────
  const finish = useCallback(() => {
    const mine  = me.engineRef.current;
    const yours = npcEngineRef.current;
    if (!mine) return;
    const myScore  = mine.score;
    const oppScore = yours?.score ?? oppSnap?.score ?? 0;
    const w = myScore >= oppScore ? "me" : "them";
    setWinner((prev) => {
      if (prev) return prev;                   // a fell already settled it
      onResult?.({
        won: w === "me", myScore, oppScore, opponent: opponent?.id,
        stats: { ...statsRef.current },        // D6
      });
      return w;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppSnap, opponent, onResult]);

  // ── Sabotage cost (table may discount it — see Spite) ───────
  const discount = table.sabotageDiscount
    ? table.sabotageDiscount(me.engineRef.current || {})
    : 1;

  const castSabotage = (powerId, point) => {
    const e = me.engineRef.current;
    if (!e) return;
    const p = getPower(powerId);
    if (!p?.sabotage) return;
    const cost = Math.round(p.cost * discount);
    if (e.charge < cost || e.phase !== PHASE.LIVE) return;

    e.charge = Math.max(0, e.charge - cost);
    e.aiming = null;
    e._aimHover = null;
    e._push("sab", `☠ ${p.name} → ${opponent?.name || "them"}`);
    statsRef.current.sabotagesSent += 1;       // D6 — counted where paid
    send(powerId, point, "me");
  };

  // ── THE MONSTER RAIL ────────────────────────────────────────
  // My avatar lives on THEIR table. These spends drive it: an
  // attack (BOSS_MOVES) hits them; BULWARK/MEND (GUARDIAN_MOVES)
  // tend it back from the brink. Same wire, same direction as a
  // sabotage — toward the enemy table, where my monster lives.
  const castMonster = (moveId, cost) => {
    const e = me.engineRef.current;
    if (!e) return;
    if (!e.spend(cost)) return;                // can't afford → no-op
    const mv = BOSS_MOVES[moveId] || GUARDIAN_MOVES[moveId];
    const guard = !!GUARDIAN_MOVES[moveId];
    e._push("sab", `${guard ? "✚" : "👹"} ${mv?.name || moveId} → your monster`);
    send(moveId, null, "me");
  };

  // Aim resolution — routes to self-cast OR sabotage depending on
  // which bar armed it. The "sab:" prefix is the whole disambiguator.
  const handleAim = (point) => {
    const id = me.snap?.aiming;
    if (!id) return;
    if (id.startsWith("sab:")) castSabotage(id.slice(4), point);
    else me.cast(id, point);
  };
  const handleAimOnTheirTable = (point) => {
    const id = me.snap?.aiming;
    if (!id || !id.startsWith("sab:")) return;
    castSabotage(id.slice(4), point);
  };

  if (!me.snap) return null;
  const snap = me.snap;
  const aimingSabotage = !!snap.aiming?.startsWith("sab:");

  const myScore  = snap.score;
  const oppScore = oppSnap?.score ?? 0;
  const lead = myScore - oppScore;

  return (
    // ── IT MUST FIT. (Same fix as SoloScreen, and worse here.) ──
    // A duel stacks a VERSUS BAR on top of TWO 400×700 playfields.
    // Sized by width, that's a page nearly a thousand pixels tall and
    // guaranteed scroll — on the one screen where you cannot afford to
    // look away, because someone is actively trying to ruin you.
    //
    // `100dvh` (dynamic viewport height — the one that survives a
    // mobile browser's vanishing address bar, unlike `100vh`), a
    // column layout, and the tables take whatever height is left after
    // the bar. Width falls out of the aspect ratio, as it should.
    <div style={{
      height: "100dvh", maxHeight: "100dvh", background: PAGE_BG,
      padding: "10px 12px", boxSizing: "border-box",
      overflow: "hidden",
      display: "flex", flexDirection: "column",
      fontFamily: UI.font,
    }}>
      {/* ── THE VERSUS BAR ─────────────────────────────────── */}
      <div style={{
        maxWidth: 1080, margin: "0 auto 10px", width: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 20, flexShrink: 0,           // ← the bar never squashes
      }}>
        <div style={{ textAlign: "right", flex: 1 }}>
          <div style={{ fontFamily: UI.fontD, fontSize: 13, letterSpacing: 3, color: UI.cool }}>
            YOU
          </div>
          <div style={{
            fontFamily: UI.fontNum, fontSize: 24, fontWeight: 700,
            color: lead >= 0 ? UI.brassBright : UI.text,
            textShadow: lead >= 0 ? glow(UI.brassBright) : "none",
          }}>
            {myScore.toLocaleString()}
          </div>
        </div>

        <div style={{
          padding: "6px 16px", clipPath: facet(6),
          background: PANEL_LIT, boxShadow: RAISED,
          fontFamily: UI.fontD, fontSize: 11, letterSpacing: 2,
          color: lead > 0 ? UI.good : lead < 0 ? UI.bad : UI.textDim,
          minWidth: 96, textAlign: "center",
        }}>
          {lead === 0 ? "EVEN" : `${lead > 0 ? "+" : ""}${lead.toLocaleString()}`}
        </div>

        <div style={{ textAlign: "left", flex: 1 }}>
          <div style={{ fontFamily: UI.fontD, fontSize: 13, letterSpacing: 3, color: UI.hot }}>
            {(opponent?.name || "OPPONENT").toUpperCase()}
          </div>
          <div style={{
            fontFamily: UI.fontNum, fontSize: 24, fontWeight: 700,
            color: lead < 0 ? UI.brassBright : UI.text,
          }}>
            {oppScore.toLocaleString()}
          </div>
        </div>
      </div>

      {/* The table row takes ALL remaining height (flex: 1) and the
          tables derive their widths from it. `minHeight: 0` is what
          actually permits a flex child to shrink below its content —
          without it the row refuses to compress and the page scrolls
          anyway, which is exactly the bug we're here to kill. */}
      <div style={{
        maxWidth: 1080, margin: "0 auto", width: "100%",
        flex: 1, minHeight: 0,
        display: "flex", gap: 16, alignItems: "stretch",
        justifyContent: "center",
      }}>

        {/* ── MY TABLE (big, warm) ─────────────────────────── */}
        {/* Height-driven, not width-driven. It was `width: 400`, which
            on a 400×700 table dictates a 700px height and blows the
            window out. */}
        <div style={{
          position: "relative", flexShrink: 1,
          height: "100%", minHeight: 0,
          aspectRatio: `${table.W} / ${table.H}`,
          maxWidth: 400,
          display: "flex", flexDirection: "column",
        }}>
          <Playfield
            engineRef={me.engineRef}
            snap={snap}
            aiming={aimingSabotage ? null : snap.aiming}
            onAim={handleAim}
          />
          <Banner snap={snap} onExit={onExit} />
        </div>

        {/* ── THE CONSOLE (center) ─────────────────────────── */}
        <div style={{
          // Scrolls INTERNALLY if it outgrows the window. It must never
          // be the thing that makes the PAGE scroll — the tables have to
          // stay put and fully visible, because a duel is the one screen
          // where looking away costs you.
          width: 190, flexShrink: 0, minHeight: 0,
          overflowY: "auto", overflowX: "hidden",
          display: "flex",
          flexDirection: "column", gap: 11,
        }}>
          <div style={{
            background: PANEL, clipPath: facet(8), padding: "10px 12px",
            boxShadow: RAISED, display: "flex", flexDirection: "column", gap: 10,
          }}>
            <ChargeMeter snap={snap} />
            <StatusStrip snap={snap} />
          </div>

          {/* SELF — cool */}
          <div>
            <div style={{
              fontFamily: UI.font, fontSize: 9, letterSpacing: 3,
              color: UI.cool, marginBottom: 6, textShadow: glow(UI.cool),
            }}>
              ⬢ YOURS
            </div>
            <PowerBar snap={snap} onCast={me.cast} onAimStart={me.beginAim} />
          </div>

          {/* SABOTAGE — hot */}
          <SabotageBar
            snap={snap}
            discount={discount}
            onSabotage={castSabotage}
            onAimStart={me.beginAim}
          />

          {/* ★ THE BEING ON MY TABLE — the opponent's avatar. Fell
              it (by playing the Cirque as normal) and I beat THEM. */}
          {snap.boss && (
            <BossBar snap={snap} label="THEIR AVATAR" />
          )}

          {/* ★ MY MONSTER — it lives on their table; this rail
              drives it. Attack them as the monster, or guard it. */}
          {oppSnap?.boss && (
            <div style={{
              background: PANEL, clipPath: facet(8), padding: "9px 11px",
              boxShadow: RAISED,
            }}>
              <MonsterBar
                snap={snap}
                oppBoss={oppSnap.boss}
                duelKit={table.boss?.duelKit || []}
                onMove={castMonster}
              />
            </div>
          )}

          <Plunger snap={snap} onDown={me.plungerDown} onUp={me.plungerUp} />

          <div style={{
            background: PANEL, clipPath: facet(8), padding: "8px 10px",
            boxShadow: RAISED,
          }}>
            <Ticker snap={snap} />
          </div>

          <button onClick={onExit} style={{ ...btnStyle(UI.chromeDim), padding: "7px 12px" }}>
            ← FORFEIT
          </button>
        </div>

        {/* ── THEIR TABLE (small, cold, tappable) ──────────── */}
        {/* A column: the label keeps its natural height (flexShrink: 0)
            and the canvas takes whatever's left. */}
        <div style={{
          flexShrink: 1, position: "relative",
          height: "100%", minHeight: 0, maxWidth: 268,
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            fontFamily: UI.font, fontSize: 9, letterSpacing: 2,
            color: aimingSabotage ? UI.hot : UI.textFaint,
            marginBottom: 6, textAlign: "center", flexShrink: 0,
            textShadow: aimingSabotage ? glow(UI.hot) : "none",
            animation: aimingSabotage ? "tilt-pulse 0.9s ease-in-out infinite" : "none",
          }}>
            {aimingSabotage ? "☠ TAP THEIR TABLE" : "THEIR TABLE"}
          </div>

          <div style={{
            outline: aimingSabotage ? `2px solid ${UI.hot}` : "none",
            outlineOffset: 3,
            borderRadius: 10,
            transition: "outline 140ms",
            flex: 1, minHeight: 0,
            display: "flex", justifyContent: "center",
          }}>
            {npcEngineRef.current ? (
              <Playfield
                engineRef={npcEngineRef}
                snap={oppSnap}
                aiming={aimingSabotage ? snap.aiming : null}
                onAim={handleAimOnTheirTable}
                hostile
              />
            ) : (
              <div style={{
                aspectRatio: `${table.W} / ${table.H}`,
                background: PANEL, borderRadius: 10,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: UI.textFaint, fontSize: 11, letterSpacing: 2,
              }}>
                AWAITING OPPONENT
              </div>
            )}
          </div>

          {/* Their status — enough to make you WANT to interfere. */}
          {oppSnap && (
            <div style={{
              marginTop: 8, background: PANEL, clipPath: facet(6),
              padding: "8px 10px", boxShadow: RAISED,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <div style={{
                display: "flex", justifyContent: "space-between",
                fontFamily: UI.font, fontSize: 9, letterSpacing: 1,
                color: UI.textFaint,
              }}>
                <span>THEIR CHARGE</span>
                <span style={{ fontFamily: UI.fontNum, color: UI.hot }}>
                  {Math.round(oppSnap.charge)}
                </span>
              </div>
              <div style={{
                height: 5, background: "#0a0c15", overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", width: `${oppSnap.charge}%`,
                  background: `linear-gradient(90deg, ${UI.hotDim}, ${UI.hot})`,
                }} />
              </div>
              <div style={{
                display: "flex", justifyContent: "space-between",
                fontFamily: UI.fontNum, fontSize: 10, color: UI.textDim,
              }}>
                <span>BALL {oppSnap.ballNum}</span>
                <span>{oppSnap.ballsLeft} LEFT</span>
                {oppSnap.combo > 1 && (
                  <span style={{ color: UI.bad }}>×{oppSnap.combo} !</span>
                )}
              </div>
            </div>
          )}

          {/* The taunt. It's talking to you. */}
          {taunt && (
            <div style={{
              position: "absolute", top: 30, left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(4,5,10,0.92)",
              padding: "7px 14px", clipPath: facetTB(5),
              boxShadow: bloom(UI.hot),
              fontFamily: UI.font, fontSize: 12, letterSpacing: 1,
              color: UI.hot, textShadow: glow(UI.hot),
              whiteSpace: "nowrap", zIndex: 20,
              animation: "tilt-float 1.6s ease-in-out infinite",
            }}>
              {taunt}
            </div>
          )}
        </div>
      </div>

      {/* ── RESULT ─────────────────────────────────────────── */}
      {winner && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 60,
          background: "rgba(4,5,10,0.9)", backdropFilter: "blur(4px)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 16,
        }}>
          <div style={{
            fontFamily: UI.fontD, fontSize: 46, fontWeight: 900, letterSpacing: 8,
            color: winner === "me" ? UI.good : UI.bad,
            textShadow: glow(winner === "me" ? UI.good : UI.bad),
          }}>
            {winner === "me" ? "VICTORY" : "DEFEAT"}
          </div>
          <div style={{ fontFamily: UI.fontNum, fontSize: 16, color: UI.textDim }}>
            {myScore.toLocaleString()} — {oppScore.toLocaleString()}
          </div>
          <button onClick={onExit} style={btnStyle(UI.cool)}>CONTINUE</button>
        </div>
      )}
    </div>
  );
}
